import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { frenchAuthError } from "@/lib/auth-errors";
import { useDevBypassRedirect } from "@/hooks/use-dev-bypass-redirect";
import { NanaeLogoSymbol } from "@/components/qv3/NanaeLogo";
import { NanaeOAuthButtons } from "@/components/qorum/NanaeOAuthButtons";
import { socialMeta } from "@/lib/seo-meta";
import { toast } from "sonner";
import {
  clearPendingInvitation,
  invitationErrorMessage,
  redeemInvitationCode,
  rememberPendingInvitation,
} from "@/lib/invitation";

/* ============================================================
 *  Inscription Hōjin — écran unique
 *  Email + mot de passe → signUp() → interstitiel /email-confirmation
 *  Le lien reçu par email atterrit sur /auth/callback puis /foyer.
 * ============================================================ */

const schema = z.object({
  email: z.string().email("Adresse email invalide"),
  password: z.string().min(8, "8 caractères minimum"),
  invitationCode: z.string().max(100, "Code d'invitation invalide").optional(),
});
type Values = z.infer<typeof schema>;

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Inscription — Hōjin" },
      { name: "description", content: "Créez votre compte Hōjin pour convoquer le Conseil de sept intelligences." },
      ...socialMeta({
        title: "Inscription — Hōjin",
        description: "Créez votre compte Hōjin pour convoquer le Conseil de sept intelligences.",
        url: "https://hojin.ai/signup",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/signup" }],
  }),
  component: SignupPage,
});

export function SignupPage() {
  useDevBypassRedirect();
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [invitationNotice, setInvitationNotice] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", invitationCode: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setInvitationNotice(null);
    const invitationCode = values.invitationCode?.trim();
    try {
      const { needsConfirmation, alreadyRegistered } = await signUp(
        values.email,
        values.password,
      );
      // Email déjà inscrit : Supabase ne distingue PAS « confirmé » de
      // « non confirmé » (anti-énumération). On envoie vers l'écran de
      // vérification avec un message non équivoque pour couvrir les deux cas
      // (Supabase ré-envoie automatiquement le lien si non confirmé).
      if (alreadyRegistered) {
        navigate({
          to: "/email-confirmation",
          search: { email: values.email, state: "ambiguous" },
        });
        return;
      }
      if (needsConfirmation) {
        if (invitationCode) rememberPendingInvitation(invitationCode);
        navigate({ to: "/email-confirmation", search: { email: values.email, state: "new" } });
      } else {
        if (invitationCode) {
          try {
            const redeemed = await redeemInvitationCode(invitationCode);
            clearPendingInvitation();
            toast.success(`Code activé — accès ${redeemed.plan}.`);
          } catch (error) {
            clearPendingInvitation();
            toast.error(invitationErrorMessage(error));
          }
        }
        navigate({ to: "/foyer" });
      }
    } catch (e) {
      setFormError(frenchAuthError(e));
    }
  });

  return (
    <div className="nanae-auth-shell qv3 qv3-dark">
      <main className="nanae-auth-main">
        <section className="nanae-auth-card" aria-labelledby="signup-title">
          <span className="nanae-auth-card-corner left">INSCRIPTION</span>

          <div className="nanae-auth-logo">
            <NanaeLogoSymbol size="lg" />
          </div>

          <p className="nanae-auth-eyebrow">§ INSCRIPTION · MMXXVI</p>
          <h1 id="signup-title" className="nanae-auth-title">
            Préparez votre saisine.
          </h1>
          <p className="nanae-auth-subtitle">Sept conseillers se tiennent prêts.</p>

          <NanaeOAuthButtons label="signup" />

          <form
            onSubmit={onSubmit}
            noValidate
            style={{ display: "flex", flexDirection: "column", gap: 18 }}
          >
            <div className="qv3-field">
              <label className="qv3-field-label" htmlFor="sg-email">E-MAIL</label>
              <input
                id="sg-email"
                type="email"
                autoComplete="email"
                className="qv3-input-lcd"
                placeholder="vous@domaine.com"
                {...register("email")}
              />
              {errors.email && <p className="qv3-field-error">— {errors.email.message}</p>}
            </div>

            <div className="qv3-field">
              <label className="qv3-field-label" htmlFor="sg-password">MOT DE PASSE</label>
              <input
                id="sg-password"
                type="password"
                autoComplete="new-password"
                className="qv3-input-lcd"
                placeholder="••••••••"
                {...register("password")}
              />
              {errors.password && <p className="qv3-field-error">— {errors.password.message}</p>}
            </div>

            <div className="qv3-field">
              <label className="qv3-field-label" htmlFor="sg-invitation">CODE D'INVITATION · FACULTATIF</label>
              <input
                id="sg-invitation"
                type="text"
                autoComplete="off"
                className="qv3-input-lcd"
                placeholder="HOJIN-BETA-2026"
                {...register("invitationCode")}
              />
              {errors.invitationCode && <p className="qv3-field-error">— {errors.invitationCode.message}</p>}
              <p className="qv3-field-hint">Le code sera vérifié après l'ouverture de votre compte.</p>
            </div>

            {invitationNotice && (
              <p role="status" className="qv3-field-error">— {invitationNotice}</p>
            )}

            {formError && (
              <p role="alert" className="nanae-auth-error">
                <span className="qv3-led" aria-hidden="true" />
                {formError}
              </p>
            )}

            <button type="submit" className="qv3-btn-hifi" disabled={isSubmitting}>
              {isSubmitting ? "Convocation…" : "Créer mon compte"}
              {!isSubmitting && <span className="qv3-btn-hifi-hint">ENTER →</span>}
            </button>
          </form>

          <div className="nanae-auth-altlinks">
            <Link to="/login" className="nanae-auth-altlink">
              Déjà inscrit ? <strong>Se connecter</strong>
            </Link>
          </div>
        </section>
      </main>
      <footer className="nanae-auth-foot" data-rp="auth-footer">SEPT VOIX, UNE DIRECTION · MMXXVI</footer>
    </div>
  );
}
